import quickFetch from 'quick-fetch';
import stableStringify from 'fast-json-stable-stringify';
import { makeChirpEarlyRequest } from 'rpc-client-utils';
import { searchCrmObjectsEarlyRequestRpc } from '../../../__generated__/chirp/com/hubspot/sales/rpc/earlyRequest/ViewServiceRpc.early';
import { convertLegacyObject } from '../CrmObject/convertLegacyObject';
import { normalizeVariables } from './sortFilterGroups';
import { readUserIdFromAuth } from './getUserId';
import { isSearchRpcGateEnabled } from './gate';
export const SALES_VIEWS_SEARCH_URL = '/sales-views/v2/crm-search/with-placeholders';
export const buildSalesViewsSearchBody = (args, userId) => Object.assign({}, args, {
  requestOptions: {
    allPropertiesFetchMode: 'latest_version',
    includeAllValues: true,
    includeCurrentUserPermissions: true
  }
}, userId != null ? {
  crmSearchRequestOptions: {
    userId
  }
} : {});
export const handleSalesViewsResult = response => ({
  total: response.total,
  offset: response.offset,
  hasMore: response.hasMore,
  results: response.results.map(convertLegacyObject)
});
export const buildSearchCrmObjectsRequest = args => args;
export const handleSearchCrmObjectsResult = response => ({
  total: response.total,
  offset: response.offset,
  hasMore: response.hasMore,
  results: response.results.map(object => ({
    objectId: object.objectId,
    objectTypeId: object.objectTypeId,
    currentUserPermissions: object.currentUserPermissions,
    properties: Object.fromEntries(Object.entries(object.properties).map(([key, value]) => {
      var _value$value, _value$sourceId, _value$source, _value$timestamp;
      return [key, {
        name: key,
        value: (_value$value = value.value) !== null && _value$value !== void 0 ? _value$value : undefined,
        sourceId: (_value$sourceId = value.sourceId) !== null && _value$sourceId !== void 0 ? _value$sourceId : undefined,
        source: (_value$source = value.source) !== null && _value$source !== void 0 ? _value$source : undefined,
        timestamp: (_value$timestamp = value.timestamp) !== null && _value$timestamp !== void 0 ? _value$timestamp : undefined
      }];
    }))
  }))
});
export const allPropertiesRequestName = (variables, context) => `hs-resources:crmSearchAllProperties:${stableStringify(normalizeVariables(variables, context))}`;
export const earlyRequestAllProperties = async ({
  variables,
  getUserId = readUserIdFromAuth
}) => {
  const requestName = allPropertiesRequestName(variables, 'early');
  if (quickFetch.getRequestStateByName(requestName)) {
    return;
  }
  if (await isSearchRpcGateEnabled()) {
    makeChirpEarlyRequest(searchCrmObjectsEarlyRequestRpc, {
      request: buildSearchCrmObjectsRequest(variables),
      earlyRequestName: requestName
    }).catch(() => undefined);
    return;
  }
  let userId;
  try {
    userId = await getUserId();
  } catch (_unused) {
    // Couldn't resolve the user (e.g. auth timed out) — skip the early request
    // rather than prime one that could return unscoped data. The resolver
    // fallback fetches with a properly resolved userId.
    return;
  }

  // Re-check after awaiting auth: a concurrent call for the same search may
  // have primed it while we waited (continuations resume sequentially, so the
  // first to reach makeEarlyRequest wins and the rest bail here).
  if (quickFetch.getRequestStateByName(requestName)) {
    return;
  }
  quickFetch.makeEarlyRequest(requestName, {
    url: quickFetch.getApiUrl(SALES_VIEWS_SEARCH_URL),
    type: 'POST',
    contentType: 'application/json',
    dataType: 'json',
    data: JSON.stringify(buildSalesViewsSearchBody(variables, userId))
  });
};
export const consumeEarlyCrmSearchAllProperties = variables => {
  const requestName = allPropertiesRequestName(variables, 'standard');
  const requestState = quickFetch.getRequestStateByName(requestName);
  if (!requestState) {
    return Promise.reject(new Error(`Early request not found: ${requestName}`));
  }
  return new Promise((resolve, reject) => {
    requestState.whenFinished(result => {
      resolve(result);
      quickFetch.removeEarlyRequest(requestName);
    });
    requestState.onError((_xhr, message) => {
      reject(new Error(`Early request failed: ${message}`));
      quickFetch.removeEarlyRequest(requestName);
    });
  }).then(handleSalesViewsResult);
};